import React, { useCallback, useEffect, useState } from "react";
import LoadError from '../../components/admin/LoadError';
import {
  Box, Chip, CircularProgress, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { clampSx, formatDate, formatMoney, shortId } from "../../utils/adminFormat";
import { toast } from "react-toastify";
import api from "../../api";
import { adminHasPermission } from "../../utils/adminPermissions";
import RefundTransactionDialog, { RefundStateChip } from "./RefundTransactionDialog";

const TYPE_COLORS = { course: "primary", mock_exam: "secondary", paper: "default", pdf_pack: "info" };

function flattenCategories(nodes, prefix = "", out = []) {
  (nodes || []).forEach((node) => {
    const label = prefix ? `${prefix} > ${node.name}` : node.name;
    out.push({ id: node.id, label });
    flattenCategories(node.all_children || node.children || [], label, out);
  });
  return out;
}

/**
 * Every paid transaction across all product types in one list — the view the four
 * product-specific tabs cannot give, because each only knows its own purchase table.
 */
export default function AllTransactions() {
  const canRefund = adminHasPermission("refund-transactions");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [type, setType] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [refundStatus, setRefundStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [refundRow, setRefundRow] = useState(null);

  useEffect(() => {
    api
      .get("admin/mock-exam/category-tree")
      .then((res) => setCategoryOptions(flattenCategories(res.data?.data || [])))
      .catch(() => setCategoryOptions([]));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const params = { page: page + 1, per_page: perPage };
      if (type) params.type = type;
      if (categoryId) params.category_id = categoryId;
      if (refundStatus) params.refund_status = refundStatus;
      const res = await api.get("admin/billing/transactions", { params });
      setRows(res.data?.data?.data || []);
      setTotal(res.data?.data?.total || 0);
    } catch (e) {
      setLoadError(e || true);
      toast.error(e.response?.data?.message || "Failed to load transactions");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, type, categoryId, refundStatus]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select size="small" label="Product type" value={type}
          onChange={(e) => { setType(e.target.value); setPage(0); }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All products</MenuItem>
          <MenuItem value="course">Courses</MenuItem>
          <MenuItem value="mock_exam">Mock exams</MenuItem>
          <MenuItem value="paper">Past papers</MenuItem>
          <MenuItem value="pdf_pack">Practice papers</MenuItem>
        </TextField>
        <TextField
          select size="small" label="Category" value={categoryId}
          onChange={(e) => { setCategoryId(e.target.value); setPage(0); }}
          sx={{ minWidth: 280 }}
          helperText="Selecting a parent also includes everything beneath it"
        >
          <MenuItem value="">All categories</MenuItem>
          {categoryOptions.map((c) => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Refunds" value={refundStatus}
          onChange={(e) => { setRefundStatus(e.target.value); setPage(0); }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All payments</MenuItem>
          <MenuItem value="refunded">Refunded (any amount)</MenuItem>
          <MenuItem value="not_refunded">Not refunded</MenuItem>
        </TextField>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : loadError ? (
        <LoadError what="transactions" error={loadError} onRetry={() => load()} />
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Transaction</TableCell>
                {canRefund && <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.purchasable_type || r.resource_type}-${r.purchase_id}`} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2">{formatDate(r.purchased_at)}</Typography>
                    {r.purchased_at && (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(String(r.purchased_at).replace(" ", "T")).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.student_name || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.student_email}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 260, maxWidth: 380 }}>
                    <Tooltip title={r.resource_name || ""} placement="top-start" enterDelay={500}>
                      <Typography variant="body2" sx={clampSx(2)}>{r.resource_name || "—"}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={r.resource_type_label} size="small" color={TYPE_COLORS[r.resource_type] || "default"} sx={{ whiteSpace: "nowrap" }} />
                  </TableCell>
                  <TableCell>{r.category_name || "—"}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                    {formatMoney(r.amount, r.currency || "GBP")}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}><RefundStateChip row={r} /></TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    {r.transaction_id ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, whiteSpace: "nowrap" }}>
                        <Tooltip title={r.transaction_id}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>{shortId(r.transaction_id)}</Typography>
                        </Tooltip>
                        <Tooltip title="Copy transaction ID">
                          <IconButton
                            size="small"
                            aria-label="Copy transaction ID"
                            onClick={() => navigator.clipboard?.writeText(r.transaction_id).then(() => toast.success("Copied"))}
                          >
                            <ContentCopyIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : "—"}
                  </TableCell>
                  {canRefund && (
                    <TableCell align="right">
                      {r.refundable || (r.refund_state === "full" && !r.access_revoked) ? (
                        <Tooltip title={r.refundable ? "Refund" : "Refund details / revoke access"} arrow>
                          <IconButton
                            size="small" color="error"
                            onClick={() => setRefundRow({ ...r, product: r.resource_name })}
                          >
                            <CurrencyExchangeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : r.refund_state !== "none" ? (
                        <Tooltip title="Refund history" arrow>
                          <IconButton size="small" onClick={() => setRefundRow({ ...r, product: r.resource_name })}>
                            <CurrencyExchangeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Not a Stripe payment — cannot be refunded here" arrow>
                          <span>
                            <IconButton size="small" disabled><CurrencyExchangeIcon fontSize="small" /></IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canRefund ? 9 : 8} align="center" sx={{ color: "text.secondary", py: 4 }}>
                    No transactions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      <RefundTransactionDialog
        open={!!refundRow}
        row={refundRow}
        onClose={() => setRefundRow(null)}
        onDone={load}
      />
    </Box>
  );
}
